import { executeGraphQlQuery } from '../helpers/storefront-graphql-helper'
import { getMultipleGraphQLIds, getSingleGraphQLId } from './shopify'

const normalizeGqlProductFields = (imageCount = 20, variantCount = 50, additionalQuery = '') => `
  availableForSale
  id
  vendor
  title
  handle
  onlineStoreUrl
  featuredImage {
    id
    src
    width
    height
    altText
  }
  images(first: ${imageCount}) {
    nodes {
      id
      src
      width
      height
      altText
    }
  }
  options {
    name
    values
  }
  priceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  compareAtPriceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  variants(first: ${variantCount}) {
    nodes {
      availableForSale
      id
      sku
      title
      quantityAvailable
      price {
        amount
        currencyCode
      }
      compareAtPrice {
        amount
        currencyCode
      }
      selectedOptions {
        name
        value
      }
      image {
        id
        src
        width
        height
        altText
      }
      weight
      weightUnit
    }
  }
  tags
  metafields(identifiers: []) {
    key
    namespace
    value
  }
  ${additionalQuery}
`

const buildProductsQuery = (ids) => {
  const variables = { ids }

  const query = `
    query Nodes($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          ${normalizeGqlProductFields()}
        }
      }
    }
  `

  return {
    query,
    variables
  }
}

const buildCollectionsQuery = (handle, filters, size) => {
  const variables = {
    handle,
    filters,
    first: size
  }

  const query = `
  query Collection($handle: String, $first: Int, $filters: [ProductFilter!]) {
    collection(handle: $handle) {
      products(first: $first, filters: $filters) {
        nodes {
          ${normalizeGqlProductFields()}
        }
      }
    }
  }
  `

  return {
    query,
    variables
  }
}

const buildRecommendationsQuery = (id, intent) => {
  const variables = {
    id,
    intent
  }

  const query = `
    query ProductRecommendations($id: ID!, $intent: ProductRecommendationIntent) {
      productRecommendations(productId: $id, intent: $intent) {
        ${normalizeGqlProductFields()}
      }
    }
  `

  return {
    query,
    variables
  }
}

const fetchProductByCollectionHandle = async (handle, filters = [], size = 10) => {
  const gqlQuery = buildCollectionsQuery(handle, filters, size)
  const products = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => body.data.collection.products.nodes)

  return products
}

const fetchProductByIds = async (ids) => {
  const gids = getMultipleGraphQLIds(ids, 'Product')
  const gqlQuery = buildProductsQuery(gids)
  const products = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => body.data.nodes)

  return products
}

const fetchRecommendedProductById = async (id, intent = 'RELATED') => {
  const gid = getSingleGraphQLId(id, 'Product')
  const gqlQuery = buildRecommendationsQuery(gid, intent)
  const products = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => body.data.productRecommendations)

  return products
}

const buildStoreAvailability = (handle) => {
  const variables = {
    handle,
    first: 250
  }

  const query = `
    query StoreAvailability($handle: String, $first: Int) {
      product(handle: $handle) {
        variants(first: $first) {
          nodes {
            id
            storeAvailability(first: $first) {
              nodes {
                available
                pickUpTime
                location {
                  name
                }
              }
            }
          }
        }
      }
    }
  `

  return {
    query,
    variables
  }
}

const fetchStoreAvailability = async(handle) => {
  const transformData = (data) =>
    Object.fromEntries(
      data.map(({ id, storeAvailability }) => [
        id.split('/').pop(),
        storeAvailability.nodes
      ])
    )
  const gqlQuery = buildStoreAvailability(handle)
  const stores = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => {
      if (body?.data?.product?.variants?.nodes) {
        return transformData(body.data.product.variants.nodes)
      } else {
        return null
      }
    })

  return stores
}

export {
  fetchProductByCollectionHandle,
  fetchRecommendedProductById,
  fetchProductByIds,
  normalizeGqlProductFields,
  fetchStoreAvailability
}
